import { isAcTerminalId } from "./ac";
import type { DefLookup } from "./catalog";
import { isPvTerminalId, oneWayDcConnections } from "./component-ports";
import { effectiveTerminals } from "./effective";
import { isSenseTerminal } from "./sense-terminal";
import type { System } from "./types";

/** Affected DC network for a transfer the solver cannot yet represent. This
 * is a conservative influence boundary, NOT an electrical conduction graph.
 * Follow both sides of DC devices because omitted charging can change their
 * source/return currents. Never cross AC, PV, sense or signal wiring. */
export function computeIsolatorModelBoundary(system: System, lookup: DefLookup): {
  nodeIds: ReadonlySet<string>; cableIds: ReadonlySet<string>;
} {
  const nodeIds = new Set<string>();
  const powerTerminals = new Set<string>();
  const adjacent = new Map<string, Array<{ nodeId: string; cableId: string }>>();
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    const terminals = effectiveTerminals(def, node);
    const present = new Set(terminals.map(t => t.id));
    if (oneWayDcConnections(def).some(([a, b]) => present.has(a) && present.has(b)) &&
        system.cables.some(c => c.fromNodeId === node.id || c.toNodeId === node.id)) nodeIds.add(node.id);
    for (const t of terminals) {
      if (t.polarity === "signal" || isAcTerminalId(t.id) || isPvTerminalId(t.id) || isSenseTerminal(def, t)) continue;
      powerTerminals.add(`${node.id}:${t.id}`);
    }
  }
  for (const c of system.cables) {
    if (!powerTerminals.has(`${c.fromNodeId}:${c.fromTerminalId}`) ||
        !powerTerminals.has(`${c.toNodeId}:${c.toTerminalId}`)) continue;
    for (const [a, b] of [[c.fromNodeId, c.toNodeId], [c.toNodeId, c.fromNodeId]]) {
      const entries = adjacent.get(a) ?? [];
      entries.push({ nodeId: b, cableId: c.id });
      adjacent.set(a, entries);
    }
  }
  const cableIds = new Set<string>();
  const queue = [...nodeIds];
  const visited = new Set(queue);
  for (let i = 0; i < queue.length; i++) {
    for (const entry of adjacent.get(queue[i]) ?? []) {
      cableIds.add(entry.cableId);
      if (!visited.has(entry.nodeId)) { visited.add(entry.nodeId); queue.push(entry.nodeId); }
    }
  }
  return { nodeIds, cableIds };
}
